/**
 * Tools Overview Component - Collapsible table showing all tools with vulnerabilities
 */

import React, { useState } from 'react';
import { ToolOverview } from '../../types';
import { ToolIcon, ChevronDownIcon, SortIcon } from '../../../../components/Icons';
import './vulnerabilities.css';

interface ToolsOverviewProps {
  tools: ToolOverview[];
}

const ToolsOverviewSection: React.FC<ToolsOverviewProps> = ({ tools }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Entries as [tool name, category, description]
    const [toolsEntries, setToolsEntries] = useState(
      Object.entries(tools)
    );
    const [sortState, setSortState] = useState<{column: 'tool_name' | 'category' | 'description' | 'vulnerabilities', direction: 'asc' | 'desc'}>({
      column: 'tool_name',
      direction: 'desc'
    });
  
    const sortEntries = (column: 'tool_name' | 'category' | 'description' | 'vulnerabilities') => {
      const direction = sortState.direction === 'asc' ? 'desc' : 'asc';
      const sorted = [...toolsEntries].sort((a, b) => {
        const aVal =
          column === 'tool_name'
            ? a[0]
            : column === 'category'
            ? a[1].category
            : column === 'description' 
            ? a[1].description 
            : a[1].vulnerabilityCount
        const bVal = 
          column === 'tool_name'
              ? b[0]
              : column === 'category'
              ? b[1].category
              : column === 'description'
              ? b[1].description
              : b[1].vulnerabilityCount
        if (aVal < bVal) return direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return direction === 'asc' ? 1 : -1;
        return 0;
      });
      setToolsEntries(sorted);
      setSortState({ column, direction });
    };
  
    // Helper func to determine icon direction and color
    const getSortIconProps = (column: 'tool_name' | 'category' | 'description' | 'vulnerabilities') => {
      const isActive = sortState.column === column;
      return {
        color: isActive ? 'white' : 'grey',
        style: { marginLeft: 4, cursor: 'pointer', verticalAlign: 'middle', height: '16px', width: '16px' },
      };
    };

  return (
    <div className="vuln-section">
      <div className="vuln-section__header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="vuln-section__title-row">
          <ToolIcon className="vuln-section__icon vuln-section__icon--purple" />
          <h2 className="vuln-section__title">Tools Overview</h2>
          <span className="vuln-section__badge">{tools.length} Total</span>
        </div>
        <button className={`vuln-section__toggle ${isExpanded ? 'vuln-section__toggle--expanded' : ''}`}>
          <ChevronDownIcon className="vuln-section__toggle-icon" />
        </button>
      </div>

      {isExpanded && (
        <div className="vuln-section__content">
          {/* Table Header */}
          <div className="tools-table__header">
            <div className="tools-table__header-cell tools-table__header-cell--name">
              Tool Name
              <SortIcon  {...getSortIconProps('tool_name')} onClick={() => sortEntries('tool_name')} />
            </div>
            <div className="tools-table__header-cell tools-table__header-cell--category">
              Category
              <SortIcon  {...getSortIconProps('category')} onClick={() => sortEntries('category')} />
            </div>
            <div className="tools-table__header-cell tools-table__header-cell--desc">
              Description
              <SortIcon  {...getSortIconProps('description')} onClick={() => sortEntries('description')} />
            </div>
            <div className="tools-table__header-cell tools-table__header-cell--vulns">
              Vulnerabilities
              <SortIcon {...getSortIconProps('vulnerabilities')} onClick={() => sortEntries('vulnerabilities')} />
            </div>
          </div>

          {/* Table Rows */}
          {toolsEntries.map(([index, tool]) => (
            <div key={index} className="tools-table__row">
              <div className="tools-table__cell tools-table__cell--name">{tool.name}</div>
              <div className="tools-table__cell tools-table__cell--category">
                <span className="tools-table__category-badge">{tool.category}</span>
              </div>
              <div className="tools-table__cell tools-table__cell--desc">
                {tool.description || 'No description'}
              </div>
              <div className="tools-table__cell tools-table__cell--vulns">
                <span className={`tools-table__vuln-badge ${tool.vulnerabilityCount > 1 ? 'tools-table__vuln-badge--error' : 'tools-table__vuln-badge--warning'}`}>
                  {tool.vulnerabilityCount}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ToolsOverviewSection;
